import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import {
  priceFieldProps,
  priceRegisterConfig,
  urlRegisterConfig,
} from './Fields/config'
import ConfirmedBooking from './Fields/ConfirmedBooking'
import Input from './Fields/RHFInput'
import TextArea from './Fields/RHFTextArea'
import StartEndAddressFields from './Fields/StartEndAddressFields'
import StartEndDateTimeFields from './Fields/StartEndDateTimeFields'
import { useDefaultDatePickerMonthYear } from './Hooks/useDefaultDates'
import type { CreateOtherTransportItemViewModel } from '../Create/models'
import type { PatchOtherTransportItemViewModel } from '../Edit/models'

import type { AutocompletePlaceParam } from 'tripPlanner/types/google'

const AUTOCOMPLETE_TYPES: AutocompletePlaceParam = []

interface Props {
  tripId: string
}

function OtherTransportFields({ tripId }: Props) {
  const intl = useIntl()
  const methods = useFormContext<
    CreateOtherTransportItemViewModel | PatchOtherTransportItemViewModel
  >()
  const { control, watch, register } = methods

  const { defaultMonth, defaultYear } = useDefaultDatePickerMonthYear({
    tripId,
  })

  const watchBooked = watch('isBooked')

  const departureDateLabel = intl.formatMessage({
    id: 'OA4w2JzAm7',
    defaultMessage: 'Departure date',
    description: 'Form label for departure date field in other transport trip item editor',
  })

  const arrivalDateLabel = intl.formatMessage({
    id: 'OYdyKIZvai',
    defaultMessage: 'Arrival date',
    description: 'Form label for arrival date field in other transport trip item editor',
  })

  const operatorLabel = intl.formatMessage({
    id: 'sBjBOPYIHo',
    defaultMessage: 'Operator',
    description: 'Form label for operator name field in other transport trip item editor',
  })

  const websiteLabel = intl.formatMessage({
    id: 'K9eCcvAct3',
    defaultMessage: 'Website',
    description: 'Form label for website URL field in other transport trip item editor',
  })

  const confirmationNumberLabel = intl.formatMessage({
    id: 'YdEgeQrZep',
    defaultMessage: 'Confirmation number',
    description: 'Form label for booking confirmation number in other transport trip item editor',
  })

  const seatLabel = intl.formatMessage({
    id: 'bykCW9iCv8',
    defaultMessage: 'Seat',
    description: 'Form label for seat field in other transport trip item editor',
  })

  const seatPlaceholder = intl.formatMessage({
    id: 'q+fkin7v1H',
    defaultMessage: 'Seat number',
    description: 'Placeholder text for seat number input in other transport trip item editor',
  })

  const priceLabel = intl.formatMessage({
    id: 'pazdQttr6l',
    defaultMessage: 'Price',
    description: 'Form label for price field in other transport trip item editor',
  })

  const additionalNotesLabel = intl.formatMessage({
    id: 'uQBZyDvytZ',
    defaultMessage: 'Additional notes',
    description: 'Form label for additional notes field in other transport trip item editor',
  })

  const notesPlaceholder = intl.formatMessage({
    id: '31/GSwF2vy',
    defaultMessage: 'Add notes about this booking',
    description: 'Placeholder text for notes textarea in other transport trip item editor',
  })

  return (
    <>
      <StartEndAddressFields
        autocompleteTypes={AUTOCOMPLETE_TYPES}
        tripId={tripId}
      />
      <StartEndDateTimeFields
        startDateLabel={departureDateLabel}
        endDateLabel={arrivalDateLabel}
        defaultMonth={defaultMonth}
        defaultYear={defaultYear}
      />
      <Input
        {...register('operatorName')}
        label={operatorLabel}
        placeholder={operatorLabel}
      />

      <Input
        {...register('url', { ...urlRegisterConfig })}
        label={websiteLabel}
        placeholder={websiteLabel}
      />

      <Controller
        name="isBooked"
        control={control}
        render={({ field }) => (
          <ConfirmedBooking
            toggled={!!field.value}
            onToggle={() => field.onChange(!field.value)}
          />
        )}
      />

      {watchBooked && (
        <>
          <Input
            {...register('confirmationCode')}
            label={confirmationNumberLabel}
            placeholder={confirmationNumberLabel}
          />

          <Input {...register('seat')} label={seatLabel} placeholder={seatPlaceholder} />
        </>
      )}

      <Input
        {...register('price', { ...priceRegisterConfig })}
        label={priceLabel}
        placeholder={priceLabel}
        {...priceFieldProps}
      />

      <TextArea
        {...register('notes')}
        placeholder={notesPlaceholder}
        label={additionalNotesLabel}
        resize="vertical"
      />
    </>
  )
}

export default OtherTransportFields
